import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { HOME_ROUTE, LOGIN_ROUTE } from '../utils/const';
import { confirmPhone, confirmPhoneCode, setPasswordReg, getClient } from '../http/userApi';
import { checkAuth, setUserName } from '../redux/slices/authSlice';
import { getClubs } from '../http/clubsApi';
import { setIdFilial, setFilialType } from '../redux/slices/clubsSlice';
import { setLogoutCheck } from '../redux/slices/interfaceSlice';
import { setCookie } from '../utils/cookies';
import { usePostAuthClientsMutation } from '../mutations/usePostAuthClientsMutation';

export const useAuth = () => {
  const location = useLocation();
  const isLogin = location.pathname === LOGIN_ROUTE;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Состояния для формы
  const [phone, setPhone] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [inputCode, setInputCode] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [passwordCheck, setPasswordCheck] = useState<string>('');
  const [inputPassword, setInputPassword] = useState<boolean>(false);
  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(false);

  const { mutateAsync: authClients, data: authData, isSuccess } = usePostAuthClientsMutation();

  // Состояния для таймера
  const [isTimerActive, setIsTimerActive] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(0);

  // Состояния для диалога
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [_stateConfirm, setStateConfirm] = useState<boolean>(false);

  useEffect(() => {
    setPasswordsMatch(password === passwordCheck);
  }, [password, passwordCheck]);

  // Обработчики формы
  const setPhoneValue = (event) => {
    const phoneNumber = event.target.value.replace(/\D/g, '');
    const formattedPhone = phoneNumber ? `+7${phoneNumber.substring(1)}` : '';
    setPhone(formattedPhone);
  };

  const setCodeValue = (event) => {
    setCode(event);
  };

  const setPasswordValue = (e) => {
    setPassword(e);
  };

  const setPasswordValueCheck = (e) => {
    setPasswordCheck(e);
  };

  // Обработчики API
  const confirmPhonePost = (phone: string) => {
    const response = confirmPhone(phone);
    return response;
  };

  const passwordPost = async (phone: string, password: string, token: string) => {
    const response = await setPasswordReg(phone, password, token);
    return response;
  };

  const authPost = async (phone: string, password: string) => {
    try {
      const authResponse = await authClients({ phone, password });
      const userToken = authResponse.data.user_token;
      setDialogMessage('Вы успешно авторизовались');
      setOpenDialog(true);
      setCookie('user_token', userToken);
      localStorage.setItem('phone', phone);
      //new version
      setCookie('phone', phone);
      localStorage.setItem('id', authResponse.data.id);
      setCookie('isAuth', 'true', 365);

      const clientRes = await getClient(userToken);
      const clubsRes = await getClubs(userToken);

      let idClub = clientRes.data.data.club.id;
      const clubsData = clubsRes.data.data.map((club) => ({
        id: club.id,
        title: club.title,
      }));
      const userName = `${clientRes.data.data.name} ${clientRes.data.data.last_name}`;

      let clubName = clientRes.data.data.club.name;
      if (clubName === undefined) {
        clubName = clubsData[0].title;
        idClub = clubsData[0].id;
      }

      localStorage.setItem('clubs', JSON.stringify(clubsData));
      localStorage.setItem('email', clientRes.data.data.email);
      localStorage.setItem('id_club', idClub);
      localStorage.setItem('club_name', clubName);

      dispatch(setUserName(userName));
      dispatch(setFilialType(clubName));
      dispatch(setIdFilial(idClub));
      dispatch(checkAuth(true));
      dispatch(setLogoutCheck(false));
      navigate(HOME_ROUTE);
    } catch (error) {
      console.error('Auth error:', error);
      if (error.response) {
        switch (error.response.status) {
          case 500:
            setDialogMessage('На сервере ведутся технические работы. Сервис временно недоступен');
            break;
          case 502:
          case 503:
          case 504:
            setDialogMessage('Сервер временно недоступен. Попробуйте позже');
            break;
          case 401:
            setDialogMessage('Ошибка авторизации. Проверьте номер телефона и пароль');
            break;
          case 403:
            setDialogMessage('Доступ запрещен. Обратитесь к администратору');
            break;
          default:
            setDialogMessage(error.response.data.error_message);
        }
      } else if (error.request) {
        // Ошибка сети - запрос был сделан, но ответ не получен
        setDialogMessage(`Ошибка сети. Проверьте подключение к интернету (${error.request})`);
      } else {
        // Что-то случилось при настройке запроса
        setDialogMessage('Ошибка авторизации. Проверьте номер телефона и пароль');
      }
      setOpenDialog(true);
    }
  };

  // Обработчики действий
  const handleDialogClose = useCallback(() => {
    setOpenDialog(false);

    if (dialogMessage === 'Вы успешно авторизовались') {
      setStateConfirm(true);
    }
  }, [dialogMessage]);

  const handleBlockSms = () => {
    setIsTimerActive(true);
    setTimer(60);

    const timerInterval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(timerInterval);
          setIsTimerActive(false);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
  };

  const handleResendCode = () => {
    confirmPhonePost(phone);
    handleBlockSms();
  };

  const loginToggle = () => {
    setInputCode(false);
    setInputPassword(false);
  };

  const click = async () => {
    if (isLogin) {
      authPost(phone, password);
    } else {
      if (!inputCode && !inputPassword) {
        setInputCode(true);
        confirmPhonePost(phone);
        handleBlockSms();
      } else if (inputCode && !inputPassword) {
        confirmPhoneCode(phone, code)
          .then(() => {
            setInputCode(false);
            setInputPassword(true);
          })
          .catch(() => {
            setDialogMessage('Неверный код из СМС');
            setOpenDialog(true);
          });
      } else if (!inputCode && inputPassword) {
        if (password === passwordCheck && password.length >= 4) {
          const token = localStorage.getItem('pass_token');
          await passwordPost(phone, password, token);
          setInputPassword(false);
          await authPost(phone, password);
        }
      }
    }
  };

  return {
    phone,
    code,
    inputCode,
    isLogin,
    password,
    passwordCheck,
    inputPassword,
    passwordsMatch,
    isTimerActive,
    timer,
    openDialog,
    setOpenDialog,
    dialogMessage,
    setPhoneValue,
    setCodeValue,
    setPasswordValue,
    setPasswordValueCheck,
    handleDialogClose,
    handleResendCode,
    loginToggle,
    click,
  };
};
